import { makeVar } from '@apollo/client'
import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import { SpecFilters } from '@deltek/specification-client/core/Widgets/SpecTable'
import { IdTitleProps, PinningProps } from './TableSettings'

export type TableSettingsState = {
    groupEnabled: boolean
    availableColumns: IdTitleProps[]
    selectedColumns: IdTitleProps[]
    groupColumns: IdTitleProps[]
    collapseColumnChecked: Record<string, boolean>
    orderBy: string
    filters: SpecFilters<ListingMatch>
    areFiltersVisible: boolean
    columnWidths?: Record<string, number>
    isTableSettingLoaded: boolean
    pinningProps: PinningProps
}

const emptyListingMatch: Partial<ListingMatch> = {
    listingId: '',
    bimLinks: [],
    catalogs: [],
    categories: [],
    displayNameWithFirmName: '',
    firmDisplayName: '',
    firmId: '',
    firmLogoUrl: '',
    firmName: '',
    type: undefined,
    images: [],
    isActive: false,
    lastUpdated: '',
    manufacturerDocs: [],
    productId: '',
    productName: '',
    productType: '',
    sectionName: '',
    submissionStatus: '',
    videos: [],
    canAutoGenerateProductSpecification: false,
    specAgentProductTypeId: 1,
}

const DEFAULT_TABLE_SETTINGS: TableSettingsState = {
    groupEnabled: true,
    availableColumns: [],
    selectedColumns: [],
    groupColumns: [],
    pinningProps: {
        firstColumnPinned: false,
        lastColumnPinned: false,
    },
    collapseColumnChecked: {},
    orderBy: '',
    filters: Object.keys(emptyListingMatch).map((key) => {
        return { id: key, value: '' }
    }),
    areFiltersVisible: false, // TODO: Implement this
    columnWidths: {},
    isTableSettingLoaded: false,
}

export const tableSettingsReactiveVar = makeVar<TableSettingsState>(
    DEFAULT_TABLE_SETTINGS
)

export const setGroupEnabled = (groupEnabled: boolean) => {
    const current = tableSettingsReactiveVar()
    tableSettingsReactiveVar({ ...current, groupEnabled })
}

export const setAvailableColumns = (availableColumns: IdTitleProps[]) => {
    const current = tableSettingsReactiveVar()
    tableSettingsReactiveVar({ ...current, availableColumns })
}

export const setSelectedColumns = (selectedColumns: IdTitleProps[]) => {
    const current = tableSettingsReactiveVar()
    const newOrderBy: string[] = []
    const orderByArray: string[] = current.orderBy.split(',')
    selectedColumns.forEach((col) => {
        const orderCol =
            orderByArray.find(
                (orderCol) => orderCol.split(' ')[0] === col.id
            ) ?? ''
        if (orderCol) {
            newOrderBy.push(orderCol)
        }
    })

    tableSettingsReactiveVar({
        ...current,
        selectedColumns,
        orderBy: newOrderBy.join(',') ?? '',
    })
}

export const setGroupColumns = (groupColumns: IdTitleProps[]) => {
    const current = tableSettingsReactiveVar()
    tableSettingsReactiveVar({ ...current, groupColumns })
}

export const setPinningProps = (pinningProps: PinningProps) => {
    const current = tableSettingsReactiveVar()
    tableSettingsReactiveVar({ ...current, pinningProps })
}

export const setCollapseColumnChecked = (
    collapseColumnChecked: Record<string, boolean>
) => {
    const current = tableSettingsReactiveVar()
    tableSettingsReactiveVar({ ...current, collapseColumnChecked })
}

export const setOrderBy = (orderBy: string) => {
    const current = tableSettingsReactiveVar()
    const orderByArray: string[] = orderBy.split(',')
    const newOrderByArray: string[] = []

    current.selectedColumns.forEach((col) => {
        const orderCol =
            orderByArray.find(
                (orderCol) => orderCol.split(' ')[0] === col.id
            ) ?? ''
        if (orderCol) {
            newOrderByArray.push(orderCol)
        }
    })

    const newOrderByIds = newOrderByArray.map((col) => col.split(' ')[0])

    const groupByIds = current.groupColumns.length
        ? `${current.groupColumns.map((group) => group.id).join(',')},`
        : ''

    const newOrderByString = newOrderByArray.length
        ? `${newOrderByArray
              .filter((col) => !groupByIds.includes(col.split(' ')[0]))
              .join(',')},`
        : ''

    const sortType = !newOrderByIds.includes('type') ? 'type,' : ''

    const sortLastUpdated = !newOrderByIds.includes('lastUpdated')
        ? 'lastUpdated desc'
        : ''

    const newOrderBy = `${groupByIds}${newOrderByString}${sortType}${sortLastUpdated}`

    tableSettingsReactiveVar({ ...current, orderBy: newOrderBy })
}

export const setFIlters = (filters: SpecFilters<ListingMatch>) => {
    const current = tableSettingsReactiveVar()
    tableSettingsReactiveVar({ ...current, filters })
}

export const setTableSettings = (
    tableSettings: Partial<TableSettingsState>
) => {
    const current = tableSettingsReactiveVar()

    tableSettingsReactiveVar({ ...current, ...tableSettings })
}

export const setColumnWidths = (columnWidth: Record<string, number>) => {
    const current = tableSettingsReactiveVar()
    tableSettingsReactiveVar({
        ...current,
        columnWidths: { ...current.columnWidths, ...columnWidth },
    })
}

export const setIsTableSettingLoaded = (isLoaded: boolean) => {
    const current = tableSettingsReactiveVar()
    tableSettingsReactiveVar({
        ...current,
        isTableSettingLoaded: isLoaded,
    })
}
